import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Notification {
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    timestamp: number;
    duration?: number; // Duration in ms before auto-dismiss (0 = never dismiss)
    isValidationError?: boolean; // Flag for validation error formatting
}

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private notificationSubject = new BehaviorSubject<Notification | null>(null);
    public notification$: Observable<Notification | null> = this.notificationSubject.asObservable();

    constructor() {}

    /**
     * Show a success notification
     */
    public success(message: string, duration?: number): void {
        this.show(message, 'success', duration);
    }

    /**
     * Show an error notification
     */
    public error(message: string, duration?: number, isValidationError?: boolean): void {
        this.show(message, 'error', duration, isValidationError);
    }

    /**
     * Show an info notification
     */
    public info(message: string, duration?: number): void {
        this.show(message, 'info', duration);
    }

    /**
     * Show a warning notification
     */
    public warning(message: string, duration?: number): void {
        this.show(message, 'warning', duration);
    }

    /**
     * Show a notification with a custom type
     */
    public show(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', duration?: number, isValidationError?: boolean): void {
        const notification: Notification = {
            message,
            type,
            timestamp: Date.now(),
            duration,
            isValidationError
        };
        this.notificationSubject.next(notification);
    }

    /**
     * Clear the current notification
     */
    public clear(): void {
        this.notificationSubject.next(null);
    }
}
